/* Same-origin editor bridge. Product variant and purchase approval belong to the host. */
(() => {
  'use strict';
  const entry=document.querySelector('[data-bv-engraving-entry]');
  if(!entry||window.BellvariEngraving)return;
  const config=JSON.parse(document.querySelector('[data-bv-engraving-config]').textContent);
  const dialog=document.querySelector('[data-bv-engraving-dialog]');
  const iframe=dialog.querySelector('iframe');
  const trigger=entry.querySelector('[data-bv-engraving-open]');
  const teaser=document.querySelector('[data-bv-engraving-teaser]');
  const teaserLabel=document.querySelector('[data-bv-engraving-teaser-label]');
  const featureImage=document.querySelector('[data-bv-engraving-feature-image]');
  const featureCaption=document.querySelector('[data-bv-engraving-feature-caption]');
  const featureMode=entry.dataset?.bvEngravingEntryMode==='feature';
  const compactMode=entry.dataset?.bvEngravingEntryMode==='compact';
  const saveToBag=config.saveAction==='add-to-bag';
  const cardTitle=entry.querySelector('[data-bv-engraving-card-title]');
  const cardPrice=entry.querySelector('[data-bv-engraving-card-price]');
  const cardDetail=entry.querySelector('[data-bv-engraving-card-detail]');
  let saveSession=null,addingSaved=false,handledCloseEvents=0;
  let returnFocus=trigger;
  const summary=entry.querySelector('[data-bv-engraving-summary]');
  const error=entry.querySelector('[data-bv-engraving-error]');
  const variantInput=document.querySelector('[data-bv-variant-id]');
  const quantityInput=document.querySelector('[data-bv-qty]');
  const variants=JSON.parse(document.querySelector('[data-bv-variants]').textContent);
  let latest=null,ready=false,loading=null,lastColour=null,lastSpec=null,personalizationId=null;
  let resolvedProduction=null,feeUnavailable=false;
  let purchaseGuard=null,designRevision=0,latestSignature=null,editorActionRevision=0,rejectedSave=false;
  // The engraved bag this page view added last ({id,spec}). Add to Bag never re-uses that design, and saving an
  // edited design replaces that bag instead of adding a second one (fix 2026-09-23).
  let inCart=null,replaceId=null;
  const snapshotSignature=value=>JSON.stringify({state:value?.state??null,confirmedAt:value?.confirmedAt??null});
  const pending=new Map();
  const letteringReady=value=>{
    try{return iframe.contentWindow?.BellvariEngravingFontGuard?.isReadyFor(value?.state)===true;}catch{return false;}
  };
  const checkedSnapshot=value=>{
    if(!value?.state?.confirmed||letteringReady(value))return {state:value.state,confirmedAt:value.confirmedAt};
    return {state:{...value.state,confirmed:false},confirmedAt:null};
  };
  const currentVariant=()=>{
    const v=variants.find(v=>String(v.id)===String(variantInput.value));
    if(!v)return null;
    const colour=/black/i.test(v.option1)?'black':/brown/i.test(v.option1)?'brown':null;
    return {id:v.id,productId:config.productId,colour,available:v.available,currency:config.currency};
  };
  const send=(type,extra={})=>iframe.contentWindow?.postMessage({channel:'bellvari-engraving-v1',type,...extra},location.origin);
  const seenColourRequests=new Set();
  function weekenderColourChoices(){
    const selected=variants.find(v=>String(v.id)===String(variantInput.value));
    return ['brown','black'].map(colour=>{
      const candidate=variants.find(v=>new RegExp(colour,'i').test(v.option1||'')&&
        (v.option2||'')===(selected?.option2||'')&&(v.option3||'')===(selected?.option3||''));
      return {colour,available:candidate?.available===true,variantId:candidate?String(candidate.id):null};
    });
  }
  function requestWeekenderColour(message){
    if(config.productId!=='weekender'||typeof message.requestId!=='string'||
      !/^[a-zA-Z0-9-]{1,80}$/.test(message.requestId)||!['brown','black'].includes(message.colour))return;
    const respond=(ok,error='')=>send('colour-result',{requestId:message.requestId,ok,error,
      colour:currentVariant()?.colour,colours:weekenderColourChoices()});
    if(seenColourRequests.has(message.requestId))return;
    seenColourRequests.add(message.requestId);
    if(seenColourRequests.size>100)seenColourRequests.delete(seenColourRequests.values().next().value);
    if(!ready||!dialog.open||addingSaved||purchaseGuard){respond(false,'Wait for the bag update to finish, then choose your colour.');return;}
    if(message.fromColour!==currentVariant()?.colour){sync();respond(false,'The bag colour changed. Review the current colour and try again.');return;}
    const choice=weekenderColourChoices().find(item=>item.colour===message.colour);
    if(!choice?.available){respond(false,'That leather colour is currently unavailable. Your design is kept.');return;}
    if(currentVariant()?.colour===message.colour){respond(true);return;}
    const target=variants.find(v=>String(v.id)===choice.variantId);
    const button=Array.from(document.querySelectorAll('[data-bv-option-value][data-bv-option-index="0"]'))
      .find(element=>element.getAttribute('data-bv-option-value')===target?.option1);
    if(!button||button.disabled){respond(false,'Close the editor and choose the bag colour above, then review your design.');return;}
    editorActionRevision++;
    button.click();
    if(String(currentVariant()?.id)!==choice.variantId){sync();respond(false,'The bag colour could not change. Choose it above and review your design again.');return;}
    sync(true);updateSummary();respond(true);
  }
  async function resolveProduction(){
    if(!config.production)return config.production;
    const production=structuredClone(config.production);
    if(production.fee?.included===true){resolvedProduction=production;feeUnavailable=false;return production;}
    const handle=production.fee?.productHandle;
    if(!handle&&production.approved!==true)return production;
    if(typeof handle!=='string'||!/^[a-z0-9-]+$/.test(handle))throw new Error('Engraving is not ready to order for this bag yet.');
    const root=window.Shopify?.routes?.root||'/';
    const feeProduct=await window.BellvariEngravingCart.xhr('GET',root+'products/'+encodeURIComponent(handle)+'.js');
    const fee=feeProduct.variants?.find(v=>String(v.id)===String(production.fee.variantId));
    if(!fee?.available||!Number.isSafeInteger(fee.price)||fee.price<=0)throw new Error('Engraving is temporarily unavailable. Remove the engraving to order the bag on its own.');
    production.currency=config.currency;
    production.fee={...production.fee,available:true,priceMinor:fee.price,currency:config.currency};
    resolvedProduction=production;feeUnavailable=false;return production;
  }
  function feeLabel(){
    if(feeUnavailable)return 'Engraving price unavailable. You can still preview your design.';
    const production=resolvedProduction;
    if(!production)return config.initialFeeLabel||'';
    const note=production.approved===true?'':' Preview only.';
    if(production.fee?.included===true)return 'Engraving included.'+note;
    return 'Engraving '+new Intl.NumberFormat(document.documentElement.lang||'en-CA',{style:'currency',currency:config.currency}).format(production.fee.priceMinor/100)+' per bag.'+note;
  }
  function showError(message){error.textContent=message;error.hidden=false;error.focus();}
  function updateSummary(){
    const s=latest?.state;
    const active=!!(s&&(s.saved||s.dirty));
    const stale=s?.saved&&(!s.confirmed||s.saved.colour!==currentVariant()?.colour);
    const included=(resolvedProduction||config.production)?.fee?.included===true;
    if(compactMode){
      if(cardTitle)cardTitle.textContent=active?'Edit personalization':'Personalize your bag';
      if(cardPrice)cardPrice.textContent=included?'Free':feeUnavailable?'Price unavailable':feeLabel();
      if(cardDetail)cardDetail.textContent=active?(s.dirty||stale?'Finish your design':s.saved.lines.join(' / ')):'Preview your initials or text.';
    }else trigger.textContent=active?'Edit engraving':included&&!featureMode?'Personalize for free':'Personalize your bag';
    summary.textContent=active?(s.dirty||stale?'Unsaved changes. Review and save your engraving.':`Engraving saved: ${s.saved.lines.join(' / ')}. ${s.saved.font==='roman'?'Classic':s.saved.font==='serif'?'Serif':'Script'} lettering.`):`Add a name, initials or a date.${included?' No extra charge.':''}`;
    summary.hidden=compactMode||(featureMode&&!active);
    if(teaserLabel)teaserLabel.textContent=active?(s.dirty||stale?'Finish your personalization':`Engraving added: ${s.saved.lines.join(' / ')} · Edit`):included?'Free personalization available':'Personalization available';
    if(featureImage){
      const colour=currentVariant()?.colour;
      const source=colour==='black'?featureImage.dataset.blackSrc:colour==='brown'?featureImage.dataset.brownSrc:null;
      if(source&&featureImage.getAttribute('src')!==source)featureImage.setAttribute('src',source);
      if(colour){
        featureImage.alt=compactMode?'':`${colour==='black'?'Black':'Brown'} ${featureImage.dataset.productLabel} — simulated personalization preview`;
        if(featureCaption)featureCaption.textContent=`${colour==='black'?'Black':'Brown'} leather · Simulated preview`;
      }
    }
    entry.querySelector('[data-bv-engraving-remove]').hidden=!active;
    entry.querySelector('[data-bv-engraving-price]').textContent=featureMode&&included&&!feeUnavailable&&(resolvedProduction||config.production)?.approved===true?'Included with your bag':feeLabel();
    dialog.querySelector('[data-bv-engraving-price]').textContent=feeLabel();
    document.querySelectorAll('[data-bv-form] .shopify-payment-button').forEach(el=>{el.hidden=active;});
  }
  function sync(open=false){
    const variant=currentVariant();
    if(!variant?.colour){showError('Engraving is not available for this colour.');return;}
    lastColour=variant.colour;
    if(ready){iframe.contentWindow?.BellvariEngravingFontGuard?.configure(variant.colour);send('configure',{colour:variant.colour,open,
      ...(config.productId==='weekender'?{colours:weekenderColourChoices()}: {})});}
  }
  async function ensureLoaded(){
    if(ready)return;
    if(loading)return loading;
    loading=new Promise((resolve,reject)=>{
      const timeout=setTimeout(()=>{pending.delete('ready');loading=null;reject(new Error('The engraving editor could not load. Please try again.'));},20000);
      pending.set('ready',()=>{clearTimeout(timeout);resolve();});
      // Shopify blocks loading a storefront URL in a frame. A local frame document
      // isolates editor styles without another storefront request or nested forms.
      const escape=value=>String(value).replace(/[&<>"']/g,c=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
      const guardUrl=new URL('bv-engraving-font-guard.js',new URL(config.editorJs,location.origin));
      guardUrl.searchParams.set('v','20260919-weekender-colour-r1');
      iframe.srcdoc=`<!doctype html><html lang="${escape(document.documentElement.lang||'en')}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><link rel="stylesheet" href="${escape(config.editorCss)}"></head><body data-embedded="true" data-product="${escape(config.productId)}"><main id="studio" tabindex="-1"></main><script type="module" src="${escape(guardUrl.href)}" data-bv-engraving-editor="${escape(config.editorJs)}" data-bv-engraving-save-action="${saveToBag?'add-to-bag':'save'}"></script></body></html>`;
    });
    return loading;
  }
  async function openEditor(source=trigger){
    if(addingSaved||purchaseGuard?.lock)return;
    error.hidden=true;
    const opener=source?.currentTarget||source;
    returnFocus=opener===teaser?teaser:trigger;
    editorActionRevision++;
    saveSession=saveToBag?{consumed:false}:null;
    if(!dialog.open)dialog.showModal();
    document.documentElement.classList.add('bv-engraving-modal-open');
    dialog.querySelector('[data-bv-engraving-close]').focus();
    try{
      await Promise.all([ensureLoaded(),resolveProduction().catch(()=>{resolvedProduction=null;feeUnavailable=true;})]);
      updateSummary();sync(true);
    }catch(e){dialog.close();showError(e.message);}
  }
  function closeEditor(){
    saveSession=null;
    if(dialog.open){handledCloseEvents++;dialog.close();}
    document.documentElement.classList.remove('bv-engraving-modal-open');returnFocus.focus();
  }
  dialog.addEventListener('close',()=>{
    // Native close events are queued. A close already handled before starting
    // the add must never restore focus over the newly opened cart drawer.
    if(handledCloseEvents){handledCloseEvents--;return;}
    if(dialog.open)return;
    saveSession=null;document.documentElement.classList.remove('bv-engraving-modal-open');returnFocus.focus();
  });
  async function addSavedDesign(){
    if(addingSaved)return;
    addingSaved=true;
    const controls=[trigger,entry.querySelector('[data-bv-engraving-remove]')].filter(Boolean);
    const previous=controls.map(element=>({element,disabled:element.disabled}));
    controls.forEach(element=>{element.disabled=true;});
    trigger.setAttribute('aria-busy','true');
    try{
      if(typeof window.bvAddToCart!=='function')throw new Error('The bag could not be added. Refresh the page and review your saved engraving before trying again.');
      // The same guarded purchase function owns the selected variant, quantity,
      // line-item proof and free gift. Never create another cart implementation.
      const result=await window.bvAddToCart(variantInput.value,quantityInput?.value||1);
      if(!result?.ok)showError(result?.error||'Your engraving is saved. Wait for the current cart update, then use Add to Bag to try again.');
    }catch(e){showError(e.message||'Your engraving is saved. Use Add to Bag to try again.');}
    finally{
      addingSaved=false;
      previous.forEach(({element,disabled})=>{element.disabled=disabled;});
      trigger.removeAttribute('aria-busy');
    }
  }
  dialog.querySelector('[data-bv-engraving-close]').addEventListener('click',closeEditor);
  trigger.addEventListener('click',openEditor);
  teaser?.addEventListener('click',event=>{
    if(purchaseGuard?.lock){event.preventDefault();return;}
    if(latest?.state&&(latest.state.saved||latest.state.dirty)){
      event.preventDefault();openEditor(teaser);
    }
  });
  entry.querySelector('[data-bv-engraving-remove]').addEventListener('click',()=>{editorActionRevision++;if(ready)send('remove');});
  window.addEventListener('message',event=>{
    if(event.origin!==location.origin||event.source!==iframe.contentWindow||event.data?.channel!=='bellvari-engraving-v1'||event.data.productId!==config.productId)return;
    const message=event.data;
    if(message.type==='colour-request'){requestWeekenderColour(message);return;}
    if(message.type==='colour-options'&&config.productId==='weekender'&&ready&&dialog.open){sync();return;}
    if(!['ready','state','saved','snapshot','closed'].includes(message.type))return;
    if(message.type==='saved'&&saveToBag&&(!dialog.open||!saveSession||saveSession.consumed))return;
    latest=checkedSnapshot(message);
    // Design removed: the next saved design is a new bag, so keep the one already in the cart.
    if(inCart&&latest.state&&!latest.state.saved)inCart.keep=true;
    const signature=snapshotSignature(latest);
    if(signature!==latestSignature){designRevision++;latestSignature=signature;}
    if(message.type==='ready'){ready=true;pending.get('ready')?.();pending.delete('ready');sync(dialog.open);}
    updateSummary();
    if(message.type==='snapshot'){pending.get(message.requestId)?.(latest);pending.delete(message.requestId);}
    if(message.type==='saved'){
      const savedMatches=latest.state?.saved&&!latest.state.dirty&&latest.state.saved.colour===currentVariant()?.colour;
      if(!latest.state?.confirmed||!letteringReady(latest)||(saveToBag&&!savedMatches)){
        rejectedSave=true;
        send('configure',{colour:currentVariant()?.colour,open:true});
        showError(saveToBag&&!savedMatches?'Review and save the engraving for your current bag before adding it.':'Wait for the lettering to load, then review and save your engraving again.');
        return;
      }
      rejectedSave=false;
      const shouldAdd=saveToBag&&dialog.open&&saveSession&&!saveSession.consumed;
      if(shouldAdd)saveSession.consumed=true;
      if(teaser)returnFocus=teaser;closeEditor();(compactMode?trigger:teaser||summary).focus();
      if(shouldAdd)void addSavedDesign();
    }
    if(message.type==='closed'){
      // The immutable editor queues a closed event after saved. A rejected save
      // must keep the requested review open when that earlier event arrives.
      if(rejectedSave){rejectedSave=false;return;}
      if(dialog.open)closeEditor();
    }
  });
  document.addEventListener('click',event=>{if(event.target.closest('[data-bv-option-value],[data-bv-colorway]'))queueMicrotask(()=>{if(currentVariant()?.colour!==lastColour)sync();updateSummary();});});
  function readSnapshot(){
    return new Promise((resolve,reject)=>{
      const requestId=crypto.randomUUID();
      const timer=setTimeout(()=>{pending.delete(requestId);reject(new Error('Review your engraving before adding the bag.'));},5000);
      pending.set(requestId,value=>{clearTimeout(timer);resolve(value);});send('snapshot',{requestId});
    });
  }
  function proofReference(selection){
    const reference=resolvedProduction?.placementBasis==='reference-image'?resolvedProduction.references?.[selection.colour]:null;
    const revision=reference?.revision||(selection.productId==='weekender'?'20260912r4':'20260911r2');
    const editorRevision=config.editorRevision||(selection.productId==='weekender'?'20260912r4':'20260911r2');
    if(revision!==editorRevision)throw new Error('The engraving setup does not match the previewed placement. Review the setup before ordering.');
    const view=revision==='20260911r2'?'engraving-studio':selection.productId==='weekender'&&revision==='20260912r4'?'engraving-studio-20260912r4':null;
    if(!view)throw new Error('The engraving preview version is unavailable. Review and save your engraving again.');
    const data={version:1,revision,productId:selection.productId,colour:selection.colour,format:selection.format,font:selection.font,lines:selection.lines};
    if(reference){data.placementBasis='reference-image';data.referenceId=reference.id;}
    const encoded=btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(data)))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
    const url=new URL(config.productUrl,location.origin);url.searchParams.set('view',view);
    const theme=(new URLSearchParams(location.search).get('preview_theme_id')||config.previewThemeId);if(theme)url.searchParams.set('preview_theme_id',theme);
    url.hash='proof='+encoded;
    return {reference:url.href,revision,...(reference?{referenceId:reference.id}:{})};
  }
  function assertPurchaseCurrent(guard,snapshot){
    if(!guard||purchaseGuard!==guard)throw new Error('Review your current engraving before adding the bag.');
    if(snapshot?.state?.saved&&snapshot.state.confirmed&&!letteringReady(snapshot)){
      throw new Error('The lettering is not ready. Review and save your engraving before adding the bag.');
    }
    const variant=currentVariant();
    if(String(variant?.id)!==guard.variantId||JSON.stringify(variant)!==guard.variantSignature){
      throw new Error('The bag colour or availability changed. Review your engraving before adding it.');
    }
    if((quantityInput?Number(quantityInput.value):null)!==guard.hostQuantity){
      throw new Error('The bag quantity changed. Review the quantity and add your bag again.');
    }
    if(editorActionRevision!==guard.editorActionRevision){
      throw new Error('The engraving editor changed while your bag was being checked. Save or remove the design before adding the bag.');
    }
    if(guard.signature!==null&&(snapshotSignature(snapshot)!==guard.signature||designRevision!==guard.revision)){
      throw new Error('Your engraving changed while we checked the bag. Review and save the current design before adding it.');
    }
    if(guard.productionSignature!==null&&JSON.stringify(resolvedProduction)!==guard.productionSignature){
      throw new Error('The engraving price or setup changed. Review your engraving before adding it.');
    }
  }
  function lockPurchaseEditor(guard){
    if(guard.lock)return;
    const controls=[trigger,entry.querySelector('[data-bv-engraving-remove]')];
    if(teaser)controls.push(teaser);
    const frameRoot=iframe.contentDocument?.documentElement;
    guard.lock={controls:controls.map(element=>({element,disabled:element.disabled})),frameInert:iframe.inert,pointerEvents:iframe.style.pointerEvents,frameRoot,rootInert:frameRoot?.inert};
    controls.forEach(element=>{element.disabled=true;});
    if(teaser){guard.lock.teaserAriaDisabled=teaser.getAttribute('aria-disabled');teaser.setAttribute('aria-disabled','true');}
    iframe.inert=true;iframe.style.pointerEvents='none';
    if(frameRoot)frameRoot.inert=true;
    if(dialog.open)dialog.querySelector('[data-bv-engraving-close]').focus();
  }
  function finishPurchase(){
    const lock=purchaseGuard?.lock;
    if(lock){
      lock.controls.forEach(({element,disabled})=>{element.disabled=disabled;});
      if(teaser){if(lock.teaserAriaDisabled===null)teaser.removeAttribute('aria-disabled');else teaser.setAttribute('aria-disabled',lock.teaserAriaDisabled);}
      iframe.inert=lock.frameInert;iframe.style.pointerEvents=lock.pointerEvents;
      if(lock.frameRoot)lock.frameRoot.inert=lock.rootInert;
    }
    purchaseGuard=null;
  }
  window.BellvariEngraving={
    async prepare({variantId,quantity}){
      finishPurchase();
      const guard={variantId:String(variantId),quantity,variantSignature:JSON.stringify(currentVariant()),hostQuantity:quantityInput?Number(quantityInput.value):null,wasReady:ready,editorActionRevision,signature:null,revision:null,productionSignature:null};
      purchaseGuard=guard;
      if(!ready){if(loading||dialog.open)return {ok:false,error:'Wait for the engraving editor to load, then save or remove your design.'};return null;}
      try{
        const fresh=await readSnapshot();const state=fresh.state;
        guard.signature=snapshotSignature(fresh);guard.revision=designRevision;
        assertPurchaseCurrent(guard,fresh);
        if(!state.saved&&!state.dirty)return null;
        // This design is already on a bag in the cart. Add to Bag adds a plain bag; only the editor's save re-adds it.
        if(!addingSaved&&inCart?.spec===JSON.stringify(state.saved))return null;
        const variant=currentVariant();
        if(String(variantId)!==String(variant?.id))return {ok:false,error:'The bag changed. Review your engraving before adding it.'};
        if(!state.saved||state.dirty||!state.confirmed||state.saved.colour!==variant.colour){await openEditor();return {ok:false,error:'Review and save your current engraving before adding this bag.'};}
        const identity=JSON.stringify(state.saved);
        if(identity!==lastSpec){personalizationId=crypto.randomUUID();lastSpec=identity;}
        const previousFee=resolvedProduction?.fee?.priceMinor;
        const production=await resolveProduction();
        if(previousFee!==undefined&&production?.fee?.priceMinor!==previousFee){updateSummary();return {ok:false,error:'The engraving price changed. Review the updated price before adding your bag.'};}
        // Fee availability is asynchronous. Do not build from a design that was
        // edited, removed, unconfirmed or replaced while the fee was loading.
        const checked=await readSnapshot();
        assertPurchaseCurrent(guard,checked);
        guard.productionSignature=JSON.stringify(resolvedProduction);
        guard.added={id:personalizationId,spec:identity};
        const result=window.BellvariEngravingContract.buildPurchaseItems({selection:state.saved,confirmed:state.confirmed,dirty:state.dirty,variant,quantity,production,personalizationId,confirmedAt:fresh.confirmedAt,proof:proofReference(state.saved)});
        return result;
      }catch(e){return {ok:false,error:e.message};}
    },
    async assertCurrent({variantId,quantity}){
      const guard=purchaseGuard;
      if(!guard||guard.variantId!==String(variantId)||guard.quantity!==quantity)throw new Error('Review your current bag and engraving before adding it.');
      assertPurchaseCurrent(guard,latest);
      // Prevent a queued editor interaction from changing the design between the
      // final snapshot and the POST. The add caller always releases this in finally.
      lockPurchaseEditor(guard);
      if(!guard.wasReady){
        if(ready||loading||dialog.open||editorActionRevision!==guard.editorActionRevision)throw new Error('The engraving editor changed while your bag was being checked. Save or remove the design before adding the bag.');
        return;
      }
      if(guard.productionSignature!==null){
        await resolveProduction();
        assertPurchaseCurrent(guard,latest);
      }
      // This is the final asynchronous read before the cart POST. A new draft on
      // the plain-bag path is a change too, so it cannot be silently omitted.
      const fresh=await readSnapshot();
      assertPurchaseCurrent(guard,fresh);
    },
    finish(){
      finishPurchase();
      // The edited design is now in the cart: remove the bag (and its kit) that it replaces.
      const id=replaceId;replaceId=null;
      if(id)window.BellvariEngravingCart?.updatePair(id,0,null);
    },
    open:openEditor,
    reportError:showError,
    // A successful add starts a fresh purchase attempt. An ambiguous response keeps its ID.
    added(){
      const next=purchaseGuard?.added;
      if(next){if(inCart&&!inCart.keep&&inCart.id!==next.id)replaceId=inCart.id;inCart=next;}
      lastSpec=null;personalizationId=null;
    }
  };
  if(new URLSearchParams(location.search).get('engraving')==='1'||location.hash==='#personalize')openEditor();
})();
